import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Textarea } from '@artosera/ui';
import {
  evenementsApi,
  useActionEvenement,
  useMessagesEvenement,
  type CorpsMessage,
} from '../../api/evenements.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from './Evenements.module.css';

export function MessagesEvenement({ id, peutEcrire }: { id: string; peutEcrire: boolean }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const liste = useMessagesEvenement(galerieSlug, id);
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [cible, setCible] = useState<CorpsMessage['cible']>('confirmes');
  const envoi = useActionEvenement(galerieSlug, (message: CorpsMessage, appel) =>
    evenementsApi.envoyer(galerieSlug, id, message, appel),
  );
  let erreur = t('commun:erreurReseau');
  if (estApiError(envoi.error)) {
    erreur = envoi.error.message;
    const details = envoi.error.details;
    if (
      envoi.error.status === 409 &&
      details &&
      typeof details === 'object' &&
      'raison' in details &&
      details.raison === 'quota_messages'
    )
      erreur = t('evenements.quotaAtteint');
  }
  return (
    <div className={styles.formulaire}>
      {liste.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {liste.data?.messages.map((m) => (
        <article key={m._id} className={styles.carte}>
          <h3>{m.sujet}</h3>
          <p>
            {new Date(m.creeLe).toLocaleString(i18n.language)} · {t(`evenements.cibles.${m.cible}`)}
          </p>
          <p>{m.corps}</p>
        </article>
      ))}
      {peutEcrire ? (
        <form
          className={styles.formulaire}
          onSubmit={(e) => {
            e.preventDefault();
            envoi.mutate(
              { sujet, corps, cible },
              {
                onSuccess: () => {
                  setSujet('');
                  setCorps('');
                },
              },
            );
          }}
        >
          <h2>{t('evenements.nouveauMessage')}</h2>
          <p>{t('evenements.quota')}</p>
          <Field htmlFor="msg-sujet" label={t('evenements.sujet')}>
            <Input
              id="msg-sujet"
              required
              maxLength={200}
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
            />
          </Field>
          <Field htmlFor="msg-corps" label={t('evenements.corps')}>
            <Textarea
              id="msg-corps"
              required
              maxLength={5000}
              value={corps}
              onChange={(e) => setCorps(e.target.value)}
            />
          </Field>
          <Field htmlFor="msg-cible" label={t('evenements.cible')}>
            <Select
              id="msg-cible"
              value={cible}
              onValueChange={(v) => setCible(v as typeof cible)}
              options={['confirmes', 'confirmes_et_attente'].map((v) => ({
                value: v,
                label: t(`evenements.cibles.${v}`),
              }))}
            />
          </Field>
          <Button type="submit" loading={envoi.isPending} disabled={!sujet.trim() || !corps.trim()}>
            {t('evenements.envoyer')}
          </Button>
          {envoi.isError ? <p role="alert">{erreur}</p> : null}
          {envoi.isSuccess ? <p role="status">{t('evenements.messageEnregistre')}</p> : null}
        </form>
      ) : null}
    </div>
  );
}
